import { Badge, Box, Container, Flex, Grid, GridItem, Heading, Table, Text } from "@chakra-ui/react"
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { FiAlertCircle, FiBriefcase, FiCalendar, FiExternalLink } from "react-icons/fi"

import useAuth from "@/hooks/useAuth"
import { Dashboard, type ClientNoRecentService, type ClientBudgetRisk, type ClientBirthday } from "@/client"

export const Route = createFileRoute("/_layout/")({
  component: ProviderUserDashboard,
})

const AlertSection = ({
  title,
  icon: Icon,
  colorPalette = "orange",
  children,
}: {
  title: string
  icon: React.ElementType
  colorPalette?: string
  children: React.ReactNode
}) => (
  <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
    <Flex
      px={4} py={3}
      bg={`${colorPalette}.subtle`}
      align="center" gap={2}
      borderBottomWidth="1px"
      borderColor={`${colorPalette}.100`}
    >
      <Icon size={14} />
      <Text fontWeight="semibold" fontSize="sm" color={`${colorPalette}.800`}>{title}</Text>
    </Flex>
    <Box>{children}</Box>
  </Box>
)

const NoRecentServiceList = ({ clients }: { clients: ClientNoRecentService[] }) => {
  const navigate = useNavigate()
  const router = useRouter()

  const clientHref = () =>
    router.buildLocation({ to: "/client" }).href

  if (clients.length === 0)
    return <Text fontSize="sm" color="green.600" px={4} py={3}>✓ All clear</Text>

  return (
    <Table.Root size="sm">
      <Table.Header>
        <Table.Row bg="gray.50">
          <Table.ColumnHeader>Client</Table.ColumnHeader>
          <Table.ColumnHeader>Service</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right">Days Ago</Table.ColumnHeader>
          <Table.ColumnHeader w="36px" />
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {clients.map((c) => (
          <Table.Row key={`${c.id}-${c.serviceItemId}`} _hover={{ bg: "gray.50" }}>
            <Table.Cell>
              <a
                href={clientHref()}
                onClick={(e) => {
                  e.preventDefault()
                  sessionStorage.setItem("selectedClientId", c.id)
                  navigate({ to: "/client" })
                }}
                style={{ textDecoration: "none" }}
              >
                <Text fontSize="sm" color="blue.600" cursor="pointer">{c.displayName}</Text>
                <Text fontSize="xs" color="gray.400">{c.providerClientId}</Text>
              </a>
            </Table.Cell>
            <Table.Cell>
              <Text fontSize="sm">{c.serviceItemName}</Text>
              <Text fontSize="xs" color="gray.400">every {c.recommendedIntervalDays}d</Text>
            </Table.Cell>
            <Table.Cell textAlign="right">
              <Badge colorPalette="orange" size="sm">
                {c.lastServiceDaysAgo == null ? "Never" : `${c.lastServiceDaysAgo}d ago`}
              </Badge>
            </Table.Cell>
            <Table.Cell textAlign="right" w="36px">
              <a
                href={clientHref()}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open in new tab"
                onClick={(e) => { sessionStorage.setItem("selectedClientId", c.id); e.stopPropagation() }}
                style={{ color: "var(--chakra-colors-gray-400)", display: "flex", alignItems: "center", padding: "4px" }}
              >
                <FiExternalLink size={12} />
              </a>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
}

const BudgetRiskList = ({ clients }: { clients: ClientBudgetRisk[] }) => {
  const navigate = useNavigate()
  const router = useRouter()

  const clientHref = () =>
    router.buildLocation({ to: "/client" }).href

  if (clients.length === 0)
    return <Text fontSize="sm" color="green.600" px={4} py={3}>✓ All clients on track</Text>

  return (
    <Table.Root size="sm">
      <Table.Header>
        <Table.Row bg="gray.50">
          <Table.ColumnHeader>Client</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right">Used</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right">Remaining</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right">Days Left</Table.ColumnHeader>
          <Table.ColumnHeader w="36px" />
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {clients.map((c) => (
          <Table.Row key={c.id} _hover={{ bg: "gray.50" }}>
            <Table.Cell>
              <a
                href={clientHref()}
                onClick={(e) => {
                  e.preventDefault()
                  sessionStorage.setItem("selectedClientId", c.id)
                  navigate({ to: "/client" })
                }}
                style={{ textDecoration: "none" }}
              >
                <Text fontSize="sm" color="blue.600" cursor="pointer">{c.displayName}</Text>
                <Text fontSize="xs" color="gray.400">{c.providerClientId}</Text>
              </a>
            </Table.Cell>
            <Table.Cell textAlign="right">
              <Badge colorPalette={c.percentUsed < 20 ? "red" : "orange"} size="sm">
                {c.percentUsed}%
              </Badge>
            </Table.Cell>
            <Table.Cell textAlign="right">
              <Text fontSize="sm">${c.remaining.toLocaleString()}</Text>
            </Table.Cell>
            <Table.Cell textAlign="right">
              <Badge colorPalette={c.daysLeft <= 14 ? "red" : "orange"} variant="subtle" size="sm">
                {c.daysLeft}d
              </Badge>
            </Table.Cell>
            <Table.Cell textAlign="right" w="36px">
              <a
                href={clientHref()}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open in new tab"
                onClick={(e) => { sessionStorage.setItem("selectedClientId", c.id); e.stopPropagation() }}
                style={{ color: "var(--chakra-colors-gray-400)", display: "flex", alignItems: "center", padding: "4px" }}
              >
                <FiExternalLink size={12} />
              </a>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
}

const BirthdayList = ({ clients }: { clients: ClientBirthday[] }) => {
  const navigate = useNavigate()
  const router = useRouter()

  const clientHref = () =>
    router.buildLocation({ to: "/client" }).href

  if (clients.length === 0)
    return <Text fontSize="sm" color="gray.400" px={4} py={3}>No upcoming birthdays</Text>

  return (
    <Table.Root size="sm">
      <Table.Body>
        {clients.map((c) => (
          <Table.Row key={c.id} _hover={{ bg: "gray.50" }}>
            <Table.Cell>
              <a
                href={clientHref()}
                onClick={(e) => {
                  e.preventDefault()
                  sessionStorage.setItem("selectedClientId", c.id)
                  navigate({ to: "/client" })
                }}
                style={{ textDecoration: "none" }}
              >
                <Text fontSize="sm" color="blue.600" cursor="pointer">{c.displayName}</Text>
                <Text fontSize="xs" color="gray.400">{c.providerClientId}</Text>
              </a>
            </Table.Cell>
            <Table.Cell textAlign="right">
              <Badge colorPalette={c.daysUntil === 0 ? "green" : "purple"} size="sm">
                {c.daysUntil === 0 ? "Today! 🎂" : `in ${c.daysUntil}d`}
              </Badge>
            </Table.Cell>
            <Table.Cell textAlign="right" w="36px">
              <a
                href={clientHref()}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open in new tab"
                onClick={(e) => { sessionStorage.setItem("selectedClientId", c.id); e.stopPropagation() }}
                style={{ color: "var(--chakra-colors-gray-400)", display: "flex", alignItems: "center", padding: "4px" }}
              >
                <FiExternalLink size={12} />
              </a>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
}

function ProviderUserDashboard() {
  const { user: currentUser } = useAuth()
  const providerId = sessionStorage.getItem("selectedProviderId") ?? ""

  const { data: stats } = useQuery({
    queryKey: ["providerUserDashboardStats", providerId],
    queryFn: () => Dashboard.getProviderUserDashboardStats({ query: { providerId } }),
    enabled: !!providerId,
    staleTime: 60_000,
  })

  const hasAnyClients = stats && (
    stats.myClientsNoRecentService.length > 0 ||
    stats.myClientsBudgetAtRisk.length > 0 ||
    stats.myClientBirthdays.length > 0
  )
  const hasActionItems = stats && (
    stats.myClientsNoRecentService.length > 0 || stats.myClientsBudgetAtRisk.length > 0
  )
  const hasBirthdays = stats && stats.myClientBirthdays.length > 0

  return (
    <Container maxW="full">
      <Box pt={12} m={4}>
        <Text fontSize="2xl" truncate maxW="sm">
          Hi, {currentUser?.full_name || currentUser?.email} 👋🏼
        </Text>
        <Text>Welcome back, nice to see you again!</Text>
      </Box>

      {stats && !hasAnyClients && (
        <Box mx={4} mt={6}>
          <Box
            borderWidth="1px"
            borderRadius="lg"
            p={5}
            bg="red.subtle"
            borderColor="red.200"
          >
            <Text fontSize="sm" color="red.700" fontWeight="medium">
              No clients matching your skillset.
            </Text>
          </Box>
        </Box>
      )}

      {stats && hasAnyClients && !hasActionItems && !hasBirthdays && (
        <Box mx={4} mt={6}>
          <Box
            borderWidth="1px"
            borderRadius="lg"
            p={5}
            bg="green.subtle"
            borderColor="green.100"
          >
            <Text fontSize="sm" color="green.700" fontWeight="medium">
              ✓ All clients are on track — no action items today.
            </Text>
          </Box>
        </Box>
      )}

      {hasActionItems && (
        <Box mx={4} mt={4}>
          <Heading size="sm" mb={3} color="gray.600" textTransform="uppercase" letterSpacing="wide" fontSize="xs">
            Action Required
          </Heading>
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={4}>
            <GridItem>
              <AlertSection
                title={`Overdue Services (${stats.myClientsNoRecentService.length})`}
                icon={FiAlertCircle}
                colorPalette="orange"
              >
                <NoRecentServiceList clients={stats.myClientsNoRecentService} />
              </AlertSection>
            </GridItem>
            <GridItem>
              <AlertSection
                title={`Budget Under-utilisation Risk (${stats.myClientsBudgetAtRisk.length})`}
                icon={FiBriefcase}
                colorPalette="red"
              >
                <BudgetRiskList clients={stats.myClientsBudgetAtRisk} />
              </AlertSection>
            </GridItem>
          </Grid>
        </Box>
      )}

      {hasBirthdays && (
        <Box mx={4} mt={6}>
          <Heading size="sm" mb={3} color="gray.600" textTransform="uppercase" letterSpacing="wide" fontSize="xs">
            Upcoming Birthdays
          </Heading>
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={4}>
            <GridItem>
              <AlertSection
                title={`Birthdays in Next 7 Days (${stats.myClientBirthdays.length})`}
                icon={FiCalendar}
                colorPalette="purple"
              >
                <BirthdayList clients={stats.myClientBirthdays} />
              </AlertSection>
            </GridItem>
          </Grid>
        </Box>
      )}
    </Container>
  )
}
